import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@core/config/app-config.token';
import { AppConfig } from '@core/config/app-config.model';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PaginatedApiResponse } from '@core/models/common/paginated-api-response.model';
import { ApiResponseHandler } from '@core/handlers/api-response.handler';
import { PushLive } from '@core/models/push-live.model';
import { ApiResponse } from '@core/models/common/api-response.model';
import { PushLiveChapter } from '../../models/push-live-chapter.model';
import { QuestionSet } from '../../models/question-set.model';
import { Lesson } from '../../models/lesson.model';
import { NextItem } from '../../models/common/next-item.model';
import { EnterPushLiveResponse } from '@core/models/enter-push-live-response.model';
import { PracticeAid } from '../../models/practice-aid.model';

@Injectable({
  providedIn: 'root',
})
export class PushLiveService {
  private readonly pushLiveUrl: string;

  constructor(
    private readonly http: HttpClient,
    @Inject(APP_CONFIG) private readonly cfg: AppConfig
  ) {
    this.pushLiveUrl = `${this.cfg.serviceApiUrl}/push-live`;
  }

  getPushLives(): Observable<PushLive[]> {
    return this.http.get<PaginatedApiResponse<PushLive[]>>(this.pushLiveUrl).pipe(
      map((response) => ApiResponseHandler.extractData<PushLive[]>(response) as PushLive[]),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getPushLive(uuid: string): Observable<PushLive> {
    return this.http.get<ApiResponse<PushLive>>(`${this.pushLiveUrl}/${uuid}`).pipe(
      map((response) => ApiResponseHandler.extractData<PushLive>(response) as PushLive),
      catchError(ApiResponseHandler.handleError)
    );
  }

  getPushLiveChapters(pushLiveUuid: string): Observable<PushLiveChapter> {
    return this.http
      .get<ApiResponse<PushLiveChapter>>(`${this.pushLiveUrl}/${pushLiveUuid}/chapters`)
      .pipe(
        map(
          (response) => ApiResponseHandler.extractData<PushLiveChapter>(response) as PushLiveChapter
        ),
        catchError(ApiResponseHandler.handleError)
      );
  }

  getQuestionSet(pushLiveUuid: string, questionSetUuid: string): Observable<QuestionSet> {
    return this.http
      .get<
        ApiResponse<QuestionSet>
      >(`${this.pushLiveUrl}/${pushLiveUuid}/questionSets/${questionSetUuid}`)
      .pipe(
        map((response) => ApiResponseHandler.extractData<QuestionSet>(response) as QuestionSet),
        catchError(ApiResponseHandler.handleError)
      );
  }

  getLesson(pushLiveUuid: string, lessonUuid: string): Observable<Lesson> {
    return this.http
      .get<ApiResponse<Lesson>>(`${this.pushLiveUrl}/${pushLiveUuid}/lessons/${lessonUuid}`)
      .pipe(
        map((response) => ApiResponseHandler.extractData<Lesson>(response) as Lesson),
        catchError(ApiResponseHandler.handleError)
      );
  }

  markPushLiveAsCompleted(
    pushLiveUuid: string,
    questionSetUuid: string
  ): Observable<NextItem | null> {
    return this.http
      .get<
        ApiResponse<NextItem | null>
      >(`${this.pushLiveUrl}/${pushLiveUuid}/update-flow/${questionSetUuid}`, {})
      .pipe(
        map(
          (response) => ApiResponseHandler.extractData<NextItem | null>(response) as NextItem | null
        ),
        catchError(ApiResponseHandler.handleError)
      );
  }

  enterPushLive(pushLiveUuid: string, userUuid: string): Observable<EnterPushLiveResponse | null> {
    return this.http
      .get<
        ApiResponse<EnterPushLiveResponse | null>
      >(`${this.cfg.serviceApiUrl}/push-access/${pushLiveUuid}/${userUuid}`, {})
      .pipe(
        map(
          (response) =>
            ApiResponseHandler.extractData<EnterPushLiveResponse | null>(
              response
            ) as EnterPushLiveResponse | null
        ),
        catchError(ApiResponseHandler.handleError)
      );
  }

  getPushLivePracticeAids(pushLiveUuid: string): Observable<PracticeAid[]> {
    return this.http
      .get<ApiResponse<PracticeAid[]>>(`${this.pushLiveUrl}/${pushLiveUuid}/practice-aids`)
      .pipe(
        map((response) => ApiResponseHandler.extractData<PracticeAid[]>(response) as PracticeAid[]),
        catchError(ApiResponseHandler.handleError)
      );
  }

  getPushLiveSlideAids(pushLiveUuid: string): Observable<PracticeAid[]> {
    return this.http
      .get<ApiResponse<PracticeAid[]>>(`${this.pushLiveUrl}/${pushLiveUuid}/slide-media`)
      .pipe(
        map((response) => ApiResponseHandler.extractData<PracticeAid[]>(response) as PracticeAid[]),
        catchError(ApiResponseHandler.handleError)
      );
  }
}
